import { ChangeDetectorRef, Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';

import {
    CalculationInputScreeningData, CompoundTranslationType, LocationInfo, OverrulingScreeningType, PolygonType,
    ReasonToSaveScreening,
    ReassessmentType, RemovalReasonTexts, RiskAssessmentStatus,
    ScreeningDetailView,
    ScreeningFlag, ScreeningHistoryItem, ScreeningLocationDetailView,
    ScreeningLocationResultItem, ScreeningLocationType, ScreeningPolygonResult, ScreeningPolygonStatus,
    ScreeningResultHistoryItem, ScreeningTriggeredBy
} from 'src/app/contracts';
import { CalculationService, CsvService, LocationService, ScreeningService } from 'src/app/services';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { ScreeningLocationPDFHelper } from './location-pdf.helper';
import { ScreeningLocationCSVHelper } from './location-csv.helper';
import { LocationDetailsHelper } from './location-details.helper';
import { EMPTY, merge, Subject } from 'rxjs';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { PollutionCauseCompound } from 'src/app/contracts/screenings/pollution-cause-compound';
import { ActivityCompound } from 'src/app/contracts/screenings/activity-compound';
import { AppConfig } from 'src/app/app.config';

@Component({
    selector: 'app-location-details',
    templateUrl: './location-details.component.html',
    styleUrls: ['./location-details.component.css'],
    providers: [ScreeningLocationPDFHelper, ScreeningLocationCSVHelper, LocationDetailsHelper]
})
export class ScreeningLocationDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('reasessModalCloseButton') public reasessModalCloseButton: any;
    @ViewChild('showScreeningResultHistoryButton') private readonly showScreeningResultHistoryButton: ElementRef;
    @ViewChild('showMissingModelCompoundButton') private readonly showMissingModelCompoundButton: ElementRef;

    // Helpers to get access to enums in the template html
    public riskAssessmentStatus = RiskAssessmentStatus; // Make enum accessible from the html-template
    public reassessmentType = ReassessmentType; // Make enum accessible from the html-template
    public screeningLocationType = ScreeningLocationType;
    public screeningPolygonStatus = ScreeningPolygonStatus;
    public polygonType = PolygonType;
    public compoundTranslationType = CompoundTranslationType;
    public overrulingScreeningType = OverrulingScreeningType;
    public reasonToSaveScreening = ReasonToSaveScreening;
    public screeningTriggeredBy = ScreeningTriggeredBy;
    public location: LocationInfo = new LocationInfo({});
    public screeningLocationDetails: ScreeningLocationDetailView = new ScreeningLocationDetailView({});
    public screeningResultHistories: Array<ScreeningResultHistoryItem>;
    public screeningHistories: Array<ScreeningHistoryItem>;
    public reassessRemark: string;
    public missingRemark = false;
    public reassessmentPolygon: PolygonType;
    public reassessToStatus: ScreeningPolygonStatus;
    public flags: ScreeningFlag = 0;
    public screeningFlag = ScreeningFlag;
    public loading = false;
    public isHistoryLoaded = false;
    public isLoadingMissingModelCompound = false;
    public screeningDetails: ScreeningDetailView = new ScreeningDetailView({});
    public exceedingFactorLimitation: number = AppConfig.overrulingScreeningRules.exceedingFactorLimitation * 100;
    public sumV1PolygonAreaExceedLimitation: number = AppConfig.overrulingScreeningRules.sumV1PolygonAreaExceedLimitation * 100;
    public sumV2PolygonAreaExceedLimitation: number = AppConfig.overrulingScreeningRules.sumV2PolygonAreaExceedLimitation * 100;

    public get v2ScreeningResults(): { [key: number]: Array<ScreeningLocationResultItem> } {
        if (!this.location) {
            return {};
        }
        return this.groupScreeningResultByPolygonArea(this.screeningLocationDetails.v2Screenings);
    }

    public get showV2LocationDetails(): boolean {

        return this.screeningLocationDetails.hasV2Polygon && this.screeningLocationDetails.v2PolygonResult.hasData
            && !this.showV1InsteadOfV2;
    }

    public get v1ScreeningResults(): { [key: number]: Array<ScreeningLocationResultItem> } {
        if (!this.location) {
            return {};
        }
        return this.groupScreeningResultByPolygonArea(this.screeningLocationDetails.v1Screenings);
    }

    public get showV1LocationDetails(): boolean {
        // return this.location.hasV1Polygon && !this.location.hasV2Polygon;
        return this.screeningLocationDetails.hasV1Polygon && this.screeningLocationDetails.v1PolygonResult.hasData
            && !this.showV2LocationDetails;
    }

    public showV1InsteadOfV2 = false;

    public pageRequest = {
        type: '',
        payload: null
    };
    public pageActions = {
        loadScreeningDetail: 'loadScreeningDetail'
    };
    public pageStates = {
        isLoadingMissingModelCompound: false
    };
    private readonly action$ = new Subject<any>();
    private readonly loadScreeningDetail = this.action$.pipe(
        filter(request => request.type === this.pageActions.loadScreeningDetail),
        switchMap(request => this.getScreeningDetails(request.payload))
    ).subscribe();

    constructor(
        @Inject(LOCALE_ID) locale,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly locationService: LocationService,
        private readonly calculationService: CalculationService,
        private readonly screeningService: ScreeningService,
        private readonly locationPDFHelper: ScreeningLocationPDFHelper,
        private readonly csvService: CsvService,
        private readonly locationCSVHelper: ScreeningLocationCSVHelper,
        private readonly locationDetailsHelper: LocationDetailsHelper,
        private readonly cdref: ChangeDetectorRef) {
        super(locale);
    }

    public ngOnInit(): void {
        this.loadData();
    }

    public ngOnDestroy(): void {
        this.loadScreeningDetail.unsubscribe();
    }

    public getRemovalReason(removalEnum): string {
        const reason = RemovalReasonTexts.get(removalEnum);
        return reason ? reason : '';
    }

    private loadData(): void {
        this.loading = true;
        const id = this.route.snapshot.paramMap.get('id');

        this.screeningService.getLocationDetails(id).subscribe(
            res => {
                this.updateScreeningLocationDetails(res);
                this.loading = false;
            },
            (err) => {
                this.loading = false;
            }
        );
    }

    private updateScreeningLocationDetails(screeningLocationDetails: ScreeningLocationDetailView): void {
        if (!screeningLocationDetails) {
            return;
        }
        screeningLocationDetails.v1Screenings.forEach(p => {
            if (p.factor.toString() === 'Infinity' || p.factor.toString() === 'NaN') {
                p.factor = 0;
            }
        });
        screeningLocationDetails.v1Screenings = screeningLocationDetails.v1Screenings.sort((a, b) => {
            if (a.factor < b.factor) {
                return 1;
            }
            if (a.factor > b.factor) {
                return -1;
            }
            return 0;
        });

        screeningLocationDetails.v2Screenings.forEach(p => {
            if (p.factor.toString() === 'Infinity' || p.factor.toString() === 'NaN') {
                p.factor = 0;
            }
        });
        screeningLocationDetails.v2Screenings = screeningLocationDetails.v2Screenings.sort((a, b) => {
            if (a.factor < b.factor) {
                return 1;
            }
            if (a.factor > b.factor) {
                return -1;
            }
            return 0;
        });

        if (screeningLocationDetails.hasV1Polygon && screeningLocationDetails.hasV2Polygon) {
            // If V2 is less than 1, V1 is higher than 1 then it is V1 factor that is shown
            // If V2 is less than 1, V1 is less than 1, then it is V2 factor that is shown
            // V2 is higher than 1, then it is always V2 factor that has to be shown
            const v2Screenings = screeningLocationDetails.v2Screenings.filter(p => !p.removed);
            const maxV2Factor = Math.max.apply(Math, v2Screenings.map(p => p.factor));
            const v1Screenings = screeningLocationDetails.v1Screenings.filter(p => !p.removed);
            const maxV1Factor = Math.max.apply(Math, v1Screenings.map(p => p.factor));

            if (maxV2Factor < 1 && maxV1Factor > 1) {
                this.showV1InsteadOfV2 = true;
            }
        }

        this.screeningLocationDetails = screeningLocationDetails;
        this.location = screeningLocationDetails.location;
        this.calculateFlags();
    }

    public getLeftBoxTitle(): string {
        if (!this.location) {
            return '-';
        }
        return this.locationDetailsHelper.getLeftBoxTitle(this.location.pollutionStatusCode);
    }

    // Yeah, its a hack
    public isNaN(value: any): boolean {
        const res = isNaN(value) || !isFinite(value);
        return res;
    }

    private calculateFlags(): void {
        this.flags = this.screeningLocationDetails.totalFlags;
    }

    public reassessmentEllipsisComment(polygonResult: ScreeningPolygonResult): string {
        if (polygonResult.reassessment != null) {
            if (polygonResult.reassessment.comment.length > 50) {
                return polygonResult.reassessment.comment.substring(0, 48) + ' ...';
            }

            return polygonResult.reassessment.comment;
        }

        return '';
    }

    public hasFlag(flag: ScreeningFlag): boolean {
        // tslint:disable-next-line: no-bitwise
        return ((this.flags & flag) === flag);
    }

    public hasAnyFlags(): boolean {
        return this.flags !== 0;
    }

    public newV1Calculation(): void {
        this.calculationService.createTempV1(
            this.location.id,
            this.location.name,
            this.location.number,
            // TODO: Get correct ScreeningData. Possibly from API
            new CalculationInputScreeningData({
                infiltration: 1.0,
                aquiferDepth: 1.0,
                distanceToAbstractionWell: 1.0
            })
        );
        this.router.navigate(['riskassessment/select-model']);
    }

    public reassess(): void {
        // Check requirements
        if (this.loading) {
            return;
        }

        if (!this.reassessRemark) {
            this.missingRemark = true;
            return;
        }

        this.loading = true;

        this.locationService.reassessPolygon(
            this.location.id,
            this.reassessmentPolygon,
            this.reassessToStatus === ScreeningPolygonStatus.NoRisk
                ? RiskAssessmentStatus.Risk : RiskAssessmentStatus.NoRisk,
            this.reassessRemark
        ).subscribe(res => {
            this.reasessModalCloseButton.nativeElement.click();
            this.loading = false;

            this.loadData();
        });

    }

    public onOpenModal(type: PolygonType, reassessToStatus: ScreeningPolygonStatus): void {
        this.reassessRemark = '';
        this.missingRemark = false;
        this.reassessmentPolygon = type;
        this.reassessToStatus = reassessToStatus;
        this.loading = false;
    }

    public getObjectKeys(obj: any): Array<string> {
        if (!obj) {
            return [];
        }
        return Object.keys(obj);
    }

    private groupScreeningResultByPolygonArea(
        screeningResults: Array<ScreeningLocationResultItem>): { [key: number]: Array<ScreeningLocationResultItem> } {
        const groups = screeningResults.reduce((item, value) => {
            item[value.polygonArea] = item[value.polygonArea] || [];
            item[value.polygonArea].push(value);
            return item;
        }, {} as { [key: number]: Array<ScreeningLocationResultItem> });

        return groups;
    }

    public filterScreeningResults(
        screeningResults: Array<ScreeningLocationResultItem>,
        compoundTranslationType: CompoundTranslationType): Array<ScreeningLocationResultItem> {
        return screeningResults.filter(p => p.compoundTranslationType === compoundTranslationType);
    }

    public filterScreeningResultHistoriesBy(
        array: Array<ScreeningResultHistoryItem>, key: any, value: any): Array<ScreeningResultHistoryItem>{
        if (!array || array.length === 0){
            return array;
        }
        return array.filter(p => p[key] === value);
    }

    public exportPDF(): void {
        this.locationPDFHelper.exportPDF(this.location.number, this.location.name,
            this.location.pollutionStatusCode, this.screeningLocationDetails,
            this.showV2LocationDetails, this.v2ScreeningResults,
            this.showV1LocationDetails, this.v1ScreeningResults);
    }

    public showScreeningResultHistoryModal(polygonType?: PolygonType, polygonArea?: number): void {
        this.showScreeningResultHistoryButton.nativeElement.click();

        const id = this.route.snapshot.paramMap.get('id');

        const $getScreeningHistories = this.screeningService.getScreeningHistories({ locationId: id })
            .pipe(
                tap((res) => {
                    this.screeningHistories = res.items.map(p => {
                        const item = new ScreeningHistoryItem(p);
                        item.formattedDate = this.locationDetailsHelper.getDateFormat(item.createdAt);
                        return item;
                    });
                })
            );

        const $getScreeningResultHistories = this.screeningService.getScreeningResultHistories({ locationId: id, polygonType, polygonArea })
            .pipe(
                tap((res) => {
                    let index = 0;
                    this.screeningResultHistories = res.items.map(p => {
                        const item = new ScreeningResultHistoryItem(p);
                        item.dataTarget = 'target' + (index++);

                        item.formattedDate = this.locationDetailsHelper.getDateFormat(item.createdAt);
                        return item;
                    });
                })
            );

        merge($getScreeningHistories, $getScreeningResultHistories).subscribe(() => this.isHistoryLoaded = true);

        this.isHistoryLoaded = true;

        this.screeningService.getScreeningResultHistories({ locationId: id, polygonType, polygonArea }).subscribe(res => {
            let index = 0;

            this.screeningResultHistories = res.items.map(p => {
                const item = new ScreeningResultHistoryItem(p);
                item.dataTarget = 'target' + (index++);
                item.formattedDate = this.locationDetailsHelper.getDateFormat(item.createdAt);
                return item;
            });
            this.isHistoryLoaded = true;
        });
    }
    public downloadCsv(): void {
        const fileName = 'Screening_Eksporter_' + this.location.number + '_' + format(new Date(), 'yyyyMMddHHmmss') + '.csv';
        const csvData = this.createCsvData();
        this.csvService.export(csvData.fieldDefs, csvData.data, csvData.ret, fileName);
    }

    public createCsvData(): any {
        return this.locationCSVHelper.createData(
            this.location.number, this.location.name,
            this.location.pollutionStatusCode,
            this.screeningLocationDetails,
            this.showV2LocationDetails, this.v2ScreeningResults,
            this.showV1LocationDetails, this.v1ScreeningResults
        );
    }

    public showMissingModelCompounds(): any {
        this.showMissingModelCompoundButton.nativeElement.click();
        this.pageRequest.type = this.pageActions.loadScreeningDetail;
        this.pageRequest.payload = this.screeningLocationDetails.id;

        this.action$.next(this.pageRequest);
    }

    private getScreeningDetails(id: any): any {
        this.pageStates.isLoadingMissingModelCompound = true;
        return this.screeningService.getScreeningDetails(id).pipe(
            tap(res => {
                this.screeningDetails = res;
            }),
            catchError(() => {
                this.pageStates.isLoadingMissingModelCompound = false;
                return EMPTY;
            }),
            finalize(() => {
                this.pageStates.isLoadingMissingModelCompound = false;
                this.cdref.markForCheck();
            })
        );
    }

    public getCompoundOriginText(origin: number): string {
        return this.locationDetailsHelper.getCompoundOriginText(origin);
    }

    public convertPullutantCauseCompoundsToString(pollutantCauses: PollutionCauseCompound[]): string {
        return pollutantCauses !== null && pollutantCauses.length > 0 ?
            pollutantCauses.map(pc => pc.compoundName).join(', ') : '';
    }

    public convertActivityCompoundsToString(activityCompounds: ActivityCompound[]): string {
        return activityCompounds !== null && activityCompounds.length > 0 ?
            activityCompounds.map(pc => pc.compoundName).join(', ') : '';
    }

    public convertScreeningFlagsToDanish(flag: ScreeningFlag): string[] {
        const flagStrings = [];

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Bly_kobber_eller_PAH) === ScreeningFlag.Bly_kobber_eller_PAH) {
            flagStrings.push('Bly, kobber eller PAH fjernet i trin 1');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Kompleks_geologi) === ScreeningFlag.Kompleks_geologi) {
            flagStrings.push('Område med kompleks geologi');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Manglende_modelstof) === ScreeningFlag.Manglende_modelstof) {
            flagStrings.push('Et eller flere forureningsstoffer mangler et modelsto');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Svag_geologi) === ScreeningFlag.Svag_geologi) {
            flagStrings.push('Svag datadækning for bestemmelse af dæklagstykkelser');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.MTBE_fjernet) === ScreeningFlag.MTBE_fjernet) {
            flagStrings.push('MTBE fjernet');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Boring_uden_ler) === ScreeningFlag.Boring_uden_ler) {
            flagStrings.push('Lokalitet har boring uden ler i en radius af 100-300 m fra kant');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Risiko_pga_vandindvinding) === ScreeningFlag.Risiko_pga_vandindvinding) {
            flagStrings.push('Lokalitet udgør en risiko fordi der ligger en vandindvindingsboring indenfor 100 m');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flag & ScreeningFlag.Kun_Olie_Benzin_Stoffer) === ScreeningFlag.Kun_Olie_Benzin_Stoffer) {
            flagStrings.push('Lokalitet er udelukkende forurenet med olie- og benzin stoffer');
        }

        if (flagStrings.length === 0) { flagStrings.push('Ingen flag på denne lokalitet'); }

        return flagStrings;
    }

    public getPollutionCauseNamesOfLog(input: PollutionCauseCompound[]): string[]{
        const result = [];
        if (!input || input.length <= 0){
            return [];
        }
        input.forEach(item => {
            if (!!item.industry.name && result.indexOf(item.industry.name) === -1){
                result.push(`${item.industry.name} - ${item.compoundName}`);
            }
        });
        return result;
    }

    public getActivityNamesOfLog(input: ActivityCompound[]): string[]{
        const result = [];
        if (!input || input.length <= 0){
            return [];
        }
        input.forEach(item => {
            if (!!item.activity.name && result.indexOf(item.activity.name) === -1){
                result.push(`${item.activity.name} - ${item.compoundName}`);
            }
        });
        return result;
    }

    public groupBy(key, array): any[]{
        const result = array.reduce((objectsByKeyValue, obj) => {
            const value = obj[key];
            objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
            return objectsByKeyValue;
        }, {});
        return result;
    }
}
